<template>
  <div>
    <ValidationObserver ref="observer">
      <div v-if="!isEdit && !isView">
        <h1 v-bg-typography="'h1'">Create Video Test</h1>
        <p v-bg-typography="'body-m'">
          This form is for creating a video test.
          <a
            href="https://braingineers.crunch.help/dashboard-faq/create-a-new-test"
            target="_blank"
            class="link"
            >Read more</a
          >.
        </p>
      </div>
      <div v-if="isEdit">
        <h1 v-bg-typography="'h1'">Edit Test</h1>
        <p v-bg-typography="'body-m'">
          This form is for Editing a test.
          <a
            href="https://braingineers.crunch.help/dashboard-faq/create-a-new-test"
            target="_blank"
            class="link"
            >Read more</a
          >.
        </p>
      </div>
      <div v-if="isView">
        <h1 v-bg-typography="'h1'">View Test</h1>
        <p v-bg-typography="'body-m'">
          This form is for viewing a test. If you want to know more about the
          next steps.
          <a
            href="https://braingineers.crunch.help/faq/research-steps-and-timelines"
            target="_blank"
            class="link"
            >Read more</a
          >.
        </p>
      </div>
      <video-upload-warning
        v-if="isView && !isVideoUploaded"
      ></video-upload-warning>
      <bg-field
        label="Test type"
        directions="Please select the type of test that you want to create. Read more about the differences between Essential and Professional tests <a target='_blank' href='https://braingineers.crunch.help/faq/essential-professional-tests'>here</a>."
        name="name"
        v-if="activeContract"
      >
        <div class="select-holder">
          <bg-big-checkbox
            class="select-item"
            label="Essential"
            :disabled="isDisabled"
            :description="`(${activeContract.available_tests.essential} tests remaining)`"
            @change="
              test.test_type === 'essential'
                ? (test.test_type = null)
                : (test.test_type = 'essential')
            "
            :value="test.test_type === 'essential'"
          >
            <bg-icon
              :class="`brain-image ${
                test.test_type === 'essential' ? 'brain-image--active' : null
              }`"
              name="allTests"
            />
          </bg-big-checkbox>
          <bg-big-checkbox
            @change="
              test.test_type === 'professional'
                ? (test.test_type = null)
                : (test.test_type = 'professional')
            "
            :value="test.test_type === 'professional'"
            :disabled="isDisabled"
            label="Professional"
            :description="`(${activeContract.available_tests.professional} tests remaining)`"
            class="select-item"
          >
            <bg-icon class="brain-image" name="brainrocket" />
          </bg-big-checkbox>
        </div>
      </bg-field>
      <div v-if="!isLoading && test.test_type !== null" class="input-holder">
        <bg-banner
          :isOpen="!popup[test.test_type]"
          :title="`0 remaining ${test.test_type.replace(/^\w/, (c) =>
            c.toUpperCase()
          )} tests`"
          :text="warningMessage"
        />
        <ValidationProvider
          ref="name"
          rules="required"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="What are we calling it? :) Make sure you include your specific topic in the title"
            label="Test name"
            name="name"
            :error="errors && errors[0]"
          >
            <bg-input
              :error="errors && errors[0]"
              :disabled="isDisabled"
              v-model="test.name"
              placeholder="Example: TVC Coca Cola Light 2020"
            />
          </bg-field>
        </ValidationProvider>
        <bg-field
          label="Upload your video"
          directions="Please upload your video in one of the following formats: MKV, MP4, or MOV.  If the video isn’t available at this moment, you can add it later to this test."
          name="file"
        >
          <bg-file-upload
            :uploaded="isVideoUploaded"
            :filename="this.test.video"
            :handleFileChange="handleFile"
          />
        </bg-field>
        <ValidationProvider
          ref="purpose"
          rules="required"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="What do you hope to get out of this test?"
            label="Purpose of the test"
            name="purpose"
            :error="errors && errors[0]"
          >
            <bg-input
              placeholder="Example: We like to see which parts of the video have a positive emotional impact on the viewers and whether there are parts that would need adjustment in the final edit."
              :disabled="isDisabled"
              :error="errors && errors[0]"
              tag="textarea"
              v-model="test.purpose"
              :rows="6"
            />
          </bg-field>
        </ValidationProvider>
        <bg-field
          directions="Please fill in a maximum of five associations for your brand. These will be added to the qualitative feedback. Click <a href='https://braingineers.crunch.help/dashboard-faq/associations' target='_blank' style='text-decoration: underline; color: #636383;'>here</a> to see the list of default associations."
          label="Associations"
          name="associations"
        >
          <bg-input
            v-for="(a, i) in test.associations"
            v-bind:key="a.id"
            class="mb w50"
            :disabled="isDisabled"
            :placeholder="placeholderAssociations[i]"
            v-model="test.associations[i].text"
          />
        </bg-field>
        <bg-field
          info="These questions are asked to the participant after the test. We include two default open questions:</br></br><ul style='color: white;'><li>1) Please describe as clear as possible the message of the video</li><li>2) Which moment of the video has the biggest impact on you and why?</li></ul>"
          directions="How often do you consider going grocery shopping?"
          label="Open questions"
          name="open_questions"
        >
          <bg-input
            v-for="(q, i) in test.open_questions"
            v-bind:key="q.id"
            class="mb"
            :disabled="isDisabled"
            v-model="test.open_questions[i].text"
          />
        </bg-field>
        <bg-field
          label="Target group"
          directions="Please select the target group."
          name="target_group"
        >
          <div class="select-holder">
            <bg-big-checkbox
              description="Dutch representative sample with equal distribution in age, gender, and education level"
              :disabled="isDisabled"
              @change="test.target_group = 'default'"
              :value="test.target_group === 'default'"
              class="select-item"
              label="Default"
            >
              <bg-icon class="brain-image" name="targetGroup" />
            </bg-big-checkbox>
            <bg-big-checkbox
              description="Please provide additional criteria for participants recruitment.  Additional costs will apply."
              :disabled="isDisabled"
              @change="test.target_group = 'custom'"
              :value="test.target_group === 'custom'"
              label="Custom"
            >
              <bg-icon class="brain-image" name="targetGroupCustom" />
            </bg-big-checkbox>
          </div>
        </bg-field>
        <ValidationProvider
          ref="target_group"
          :rules="test.target_group === 'custom' ? 'required' : ''"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="Participants with a mortgage, females only, dog owners, etc. Please specify down below. The target group and costs will be discussed during the kickoff call. "
            label="Custom group details"
            name="target_group"
            :error="errors && errors[0]"
            v-if="test.target_group === 'custom'"
          >
            <bg-input
              :error="errors && errors[0]"
              :disabled="isDisabled"
              v-model="test.custom_target"
              tag="textarea"
              :rows="3"
            />
          </bg-field>
        </ValidationProvider>

        <bg-field
          info
          directions="Please describe your wishes regarding planning."
          label="Do you have a preferred deadline date?"
          name="timing"
          :error="errors && errors[0]"
        >
          <bg-input
            :disabled="isDisabled"
            :error="errors && errors[0]"
            tag="textarea"
            v-model="test.timing"
            :rows="6"
          />
        </bg-field>

        <addons-management
          :addons="test.addons"
          :handleAddonUpdateCallback="handleAddonUpdate"
          :possibleAddons="{
            tagon: 'Tagon (+2500)',
            individual_participant: 'Individual participant data (+900)',
          }"
          :disabled="isDisabled"
        ></addons-management>
        <bg-field
          directions="Don't worry if you're not 100% set. Our consultants will give you a call when you click 'Request test' to advice you and finalize your test setup."
          label="Ready?"
        ></bg-field>
        <div class="action-container">
          <div v-if="isEdit">
            <bg-button
              v-if="test.state === 'draft'"
              variant="destructive"
              class="action"
              text="Delete"
              @click="deleteTest"
            />
            <bg-button
              variant="secondary"
              :isLoading="isSavingDraft"
              class="action"
              text="Save as draft"
              @click="saveAsDraft"
            />
            <bg-button
              v-if="popup[test.test_type]"
              :isLoading="isRequestingTest"
              class="action"
              @click="saveAsRequest"
              text="Request test"
            />
          </div>
          <div v-if="!isEdit && !isView">
            <bg-button
              class="action"
              variant="secondary"
              text="Save as draft"
              :isLoading="isSavingDraft"
              @click="saveAsDraft"
            />
            <bg-button
              v-if="popup[test.test_type]"
              :isLoading="isRequestingTest"
              class="action"
              @click="saveAsRequest"
              text="Request test"
            />
          </div>
          <div v-if="isView && (!isVideoUploaded || newVideoUploaded)">
            <bg-button
              :isLoading="isRequestingTest"
              class="action"
              @click="saveNewVideo"
              text="Save Video"
            />
          </div>
        </div>
      </div>
      <bg-loader :dark="true" v-if="isLoading"></bg-loader>
    </ValidationObserver>
  </div>
</template>

<style lang="scss" scoped>
.input-holder {
  max-width: 800px;
}
.select-holder {
  display: flex;
}

.select-item {
  margin-right: 20px;
}
.action-container {
  display: flex;
  margin-bottom: 200px;
}
.action {
  margin-right: 20px;
}
.pick-icon {
  height: 45px;
  width: 45px;
  margin-bottom: 1rem;
}
.brain-image {
  height: 90px;
  width: 90px;
  margin-bottom: 20px;
}

.brain-image--active {
  color: white;
}
.mb {
  margin-bottom: 10px;
}
.w50 {
  width: 50%;
}

.img {
  margin-bottom: -65px;
}

.link {
  color: var(--bg-color-text-link);
}

// test to see if mergin works
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgBigCheckbox from "@/components/bg-big-checkbox";
import AddonsManagement from "@/components/addons-management";
import BgButton from "@/components/bg-button";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import BgLoader from "@/components/bg-loader";
import BgBanner from "@/components/bg-banner";
import BgIcon from "@/components/bg-icon";
import BgFileUpload from "@/components/bg-file-upload";
import VideoUploadWarning from "@/components/video-upload-warning";
import { mapState } from "vuex";
import API from "@/api";

export default {
  name: "BgTests",
  components: {
    BgField,
    BgInput,
    BgBigCheckbox,
    BgButton,
    BgLoader,
    BgIcon,
    BgBanner,
    BgFileUpload,
    AddonsManagement,
    VideoUploadWarning,
  },
  directives: {
    BgTypography,
  },
  async created() {
    if (this.$route.params.id) {
      this.isLoading = true;
      const testData = await API.test.getTest(this.$route.params.id);
      if (testData.state !== "draft") {
        this.isDisabled = true;
        this.isView = true;
      } else {
        this.isEdit = true;
      }
      this.test = this.formatTest(testData);
      this.isVideoUploaded = this.test.video !== "/videos/fallback/default.mp4";
    }
    const companyData = await API.user.getCompany(
      this.userState.user.company.id
    );

    const activeContract = companyData.contracts.find(
      (contract) => contract.is_active === true
    );

    this.activeContract = activeContract;
    this.popup = {
      essential: activeContract.available_tests.essential > 0,
      professional: activeContract.available_tests.professional > 0,
    };
    if (!this.popup.essential && this.popup.professional) {
      this.warningMessage =
        "You have run out of Essential tests. You can Save this Essential test as a draft and one of our team members will get in touch with you for the next steps.";
    }
    if (this.popup.essential && !this.popup.professional) {
      this.warningMessage =
        "You have run out of Professional tests. You can Save this Professional test as a draft and one of our team members will get in touch with you for the next steps.";
    }

    this.isLoading = false;
  },
  methods: {
    formatTest(testData) {
      testData.open_questions = this.formatQuestions(
        testData.open_questions,
        3
      );
      testData.associations = this.formatQuestions(testData.associations, 5);

      return testData;
    },
    handleAddonUpdate(newAddonsData) {
      this.test.addons = newAddonsData;
    },
    formatQuestions(questions, maxQuestions) {
      questions = questions.filter((q) => !q.default);

      for (const index of Array(maxQuestions).keys()) {
        if (questions[index] === undefined) {
          questions[index] = { id: null, text: "" };
        }
      }

      return questions;
    },
    handleFile(file) {
      this.newVideoUploaded = true;
      this.formData.append("test[video]", file);
    },
    saveAsRequest() {
      this.isRequestingTest = true;
      this.test.state = "requested";
      this.test.company_id = this.userState.user.company.id;
      this.$refs.observer.validate().then((res) => {
        if (res) {
          this.formatFormData(this.test);
          if (!this.test.updated_at) {
            API.test.createTestWithFiles(this.formData).then(() => {
              this.$router.push({ path: `/success/` });
            });
          } else {
            API.test
              .updateTestWithFiles(this.test.id, this.formData)
              .then(() => {
                this.$router.push({ path: `/success/` });
              });
          }
        } else {
          this.isRequestingTest = false;
        }
      });
    },
    saveAsDraft() {
      this.isSavingDraft = true;
      this.test.state = "draft";
      this.test.company_id = this.userState.user.company.id;
      this.$refs.name.validate().then((res) => {
        if (res.valid) {
          this.formatFormData();
          if (!this.test.updated_at) {
            API.test.createTestWithFiles(this.formData).then((res) => {
              this.res = res;
              setTimeout(() => {
                this.$router.push({ path: `/tests/` });
              }, 1000);
            });
          } else {
            API.test
              .updateTestWithFiles(this.test.id, this.formData)
              .then(() => {
                setTimeout(() => {
                  this.$router.push({ path: `/tests/` });
                }, 1000);
              });
          }
        } else {
          this.isSavingDraft = false;
        }
      });
    },
    saveNewVideo() {
      this.isRequestingTest = true;
      API.test.updateTestWithFiles(this.test.id, this.formData).then(() => {
        setTimeout(() => {
          this.$router.push({ path: `/tests/` });
        }, 1000);
      });
    },
    formatFormData() {
      Object.keys(this.test).forEach((key) => {
        // video field is handled by the event handler
        // and we filter out null values here, as they fail on backend
        if (key === "video" || this.test[key] === null) {
          return false;
        }
        // appending all of the keys to formData, except questions and associations
        if (
          key !== "open_questions" &&
          key !== "associations" &&
          key !== "addons"
        ) {
          return this.formData.append(`test[${key}]`, this.test[key]);
        }
        // handling addons
        if (key === "addons") {
          if (this.test[key].length === 0) {
            this.formData.append(`test[${key}][]`, "");
            return;
          }
          this.test[key].forEach((value) => {
            this.formData.append(`test[${key}][]`, value);
          });
          return false;
        }
        // handling questions and associations here
        this.test[key].forEach((value, index) => {
          this.formData.append(
            `test[${key}][${index}][text]`,
            this.test[key][index].text
          );
          if (this.test[key][index].text.trim() === "") {
            this.formData.append(`test[${key}][${index}][_destroy]`, "1");
          }
          if (this.test[key][index].id && this.test[key][index].id !== null) {
            this.formData.append(
              `test[${key}][${index}][id]`,
              this.test[key][index].id
            );
          }
        });
      });
    },
    deleteTest() {
      API.test.deleteTest(this.test.id).then(() => {
        this.$router.push({ path: `/tests/` });
      });
    },
    handlePopupClose(test_type) {
      return () => {
        this.popup[test_type] = true;
      };
    },
  },
  computed: {
    ...mapState({
      userState: (state) => state.user,
    }),
  },
  data() {
    return {
      isLoading: true,
      isEdit: false,
      isView: false,
      isDisabled: false,
      isRequestingTest: false,
      isSavingDraft: false,
      popupNotClosed: true,
      warningMessage: null,
      activeContract: null,
      formData: new FormData(),
      res: null,
      isIndividualParticipantChecked: true,
      newVideoUploaded: false,
      isVideoUploaded: false,
      popup: {
        essential: null,
        professional: null,
      },
      test: {
        test_type: null,
        associations: [
          { text: "" },
          { text: "" },
          { text: "" },
          { text: "" },
          { text: "" },
        ],
        open_questions: [{ text: "" }, { text: "" }, { text: "" }],
        company_id: 0,
        target_group: "default",
        platform: "video",
        addons: [],
      },
      images: {
        mobile: require("@/assets/imgs/placeholder_mobile.svg"),
        desktop: require("@/assets/imgs/placeholder_desktop.svg"),
      },
      placeholderAssociations: [
        "Example: classy",
        "Example: easy",
        "Example: fun",
      ],
    };
  },
  mounted() {
    this.test.requester_id = this.userState.user.id;
  },
};
</script>
